/* eslint-disable no-console */
import React, { useState, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Link } from 'react-router-dom'
import {
  AppIcon,
  Modal,
  ModalButton
} from '@splunk-iteng/splunk-shared-components'
import {
  ApplicationDataInput,
  useGetAdminAppsQuery,
  useGetAppAttributeQuery,
  useAddAppAttributeMutation
} from '../../graphql/graphql'
import { CardZone } from './EditingZone'

type AppList = {
  appName: string
  appSSOid: string
  published: string
}
type AppAttribute = {
  app_name: string
  attribute_order: number
  attribute_is_required: boolean
  attribute_key: string
  attribute_value: string
  attribute_type: string
}

export function AttributeManagement() {
  const [application, setApplication] = useState('')
  const [appAttribute, setAppAttribute] = useState<AppAttribute[]>([])
  const [appList, setAppList] = useState<AppList[]>([])
  const [modalVisibility, setModalVisibility] = useState(false)
  const [AttributeManagementMutation] = useAddAppAttributeMutation()
  const { error: adminError, data: adminData } = useGetAdminAppsQuery({
    fetchPolicy: 'no-cache',
    variables: {}
  })
  const {
    error: appAttributeError,
    data: appAttributeData,
    refetch: refetchGetAppAttribute
  } = useGetAppAttributeQuery({
    fetchPolicy: 'no-cache',
    variables: { appName: application },
    skip: !application || application === ''
  })

  const modalHeader = `Publish this form?`
  const modalText = `When published, the form will be visible to customers. Are you sure you want to publish?`

  const closeModal = () => {
    setModalVisibility(false)
  }

  const handleSubmit = async () => {
    const appData: ApplicationDataInput[] =
      appAttribute.length > 0
        ? appAttribute.map((row) => {
            return {
              app_name: application,
              attribute_order: row.attribute_order,
              attribute_is_required: row.attribute_is_required,
              attribute_key: row.attribute_key,
              attribute_value: row.attribute_value,
              attribute_type: row.attribute_type
            }
          })
        : [
            {
              app_name: application,
              attribute_order: 0,
              attribute_is_required: true,
              attribute_key: 'delete',
              attribute_value: 'delete',
              attribute_type: 'delete'
            }
          ]

    await AttributeManagementMutation({
      variables: {
        data: appData
      },
      onCompleted: () => {
        alert('Success')
      },
      onError: (err) => {
        alert(err.message)
      }
    })

    closeModal()
  }

  const modalButtons: ModalButton[] = [
    {
      label: 'Cancel',
      onClick: () => {
        setModalVisibility(false)
      },
      className: 'splunk-btn-common splunk-btn-magenta'
    },
    {
      label: 'Confirm',
      onClick: () => {
        handleSubmit()
      },
      className: 'splunk-btn-common splunk-btn-magenta-solid'
    }
  ]

  useEffect(() => {
    if (appAttributeError) alert(appAttributeError.message)
    if (adminError) alert(adminError.message)
  }, [appAttributeError, adminError])

  useEffect(() => {
    if (appAttributeData?.getAppAttribute?.data) {
      const existingApp = appAttributeData?.getAppAttribute?.data
      setAppAttribute(existingApp)
    }
  }, [appAttributeData])

  useEffect(() => {
    setAppList([])
    if (adminData?.getAdminApps?.data) {
      const existingApp = adminData?.getAdminApps?.data.apps!
      setAppList(existingApp)
      if (!application && existingApp?.length > 0) {
        setApplication(existingApp[0]!.appName!)
      }
    }
  }, [adminData, application])

  const preventEnter = async (e: React.FormEvent) => {
    e.preventDefault()
  }

  const dataToPublish = (cardData: any[]) => {
    cardData.map((card) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, idlessCard } = card
      return { ...idlessCard, app_name: application }
    })
    setAppAttribute(cardData)
  }

  return (
    <form onSubmit={preventEnter}>
      <Modal
        name="ModalName"
        isActive={modalVisibility}
        options={{
          header: modalHeader,
          text: modalText,
          buttons: modalButtons,
          root: 'content'
        }}
        onSubmitCallback={async () => {
          console.log('modal clicked')
        }}
        onCloseCallback={closeModal}
      />
      <div className="attribute-page-h1-container">
        {appList.map((app) => (
          <button
            key={uuidv4()}
            type="button"
            className={`carousel-btn ${
              app.appName === application
                ? 'carousel-btn-active'
                : 'carousel-btn-inactive'
            }`}
            onClick={() => {
              setApplication(app.appName)
              refetchGetAppAttribute()
            }}
            style={{ marginRight: 10 }}
          >
            <AppIcon />
            <span>{app.appName}</span>
          </button>
        ))}
      </div>
      <div className="attribute-page-h2">
        <div className="attribute-page-h2-container">
          <span className="attribute-page-h1">{application}</span>
          <br />
          <span>
            Collect essential information from new customers during the signup
            process
          </span>
        </div>
        <Link
          className="btn btn-primary"
          to={{
            pathname: '/attribute-management/abac-preview',
            search: `?AppName=${encodeURIComponent(application)}`
          }}
          target="_blank"
        >
          <span>Preview</span>
        </Link>
        <button
          type="button"
          className="attribute-remove-btn"
          onClick={() => {
            setAppAttribute([])
          }}
          style={{ marginRight: 10 }}
        >
          Delete Form
        </button>
        <button
          type="button"
          className="attribute-publish-btn"
          onClick={() => {
            setModalVisibility(!modalVisibility)
          }}
        >
          Publish Form
        </button>
      </div>
      <div className="attribute-page-h2-container">
        <span className="attribute-page-h1">
          Customize the questions to collect during the Sign-Up flow
        </span>
        <br />
        <span>
          Create the questions new users will answer during registration. This
          will help you collect the relevant info from users.
        </span>
      </div>
      <div className="drag-drop-zone">
        <CardZone
          appName={application}
          existingForm={appAttribute}
          onSave={dataToPublish}
        />
      </div>
    </form>
  )
}
